import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { forwardRef, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSpinDelay } from 'spin-delay';
import { Icon } from '../Icon';
import { useLocalizedStrings } from '../LocalizationProvider';
import { Spinner } from '../Spinner';
import { cn } from '../utils/tailwind';
import { AutosizedInput } from './AutosizedInput';
import { AutosizedTextarea } from './AutosizedTextarea';
import styles from './InlineEditableText.module.css';
import { getCurrencySymbol, inputFormatToUtc, utcToInputFormat } from './utils';
const DateDisplay = lazy(() => import('../DateDisplay').then(module => ({ default: module.DateDisplay })));
const CurrencyDisplay = lazy(() => import('../CurrencyDisplay').then(module => ({ default: module.CurrencyDisplay })));
export const InlineEditableText = React.memo(forwardRef(({ value = '', label = '', placeholder = '', as: Component = 'span', className, disabled = false, readOnly = false, required = false, onSave, onCancel, error, ariaLabel = label, isNumeric = false, isCurrency = false, currency = 'USD', formatPattern, mask = "_", decimalScale = 2, thousandSeparator = true, decimalSeparator = ".", allowNegative = true, displayFormatter, valueIsNumericString = false, numberFormatProps, multiline = false, type = 'text', }, ref) => {
    const L = useLocalizedStrings();
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(() => {
        return value ?? '';
    });
    const [isLoading, setIsLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [internalError, setInternalError] = useState(null);
    const showError = error?.hasError || !!internalError;
    const errorMessage = internalError || error?.errorMessage || '';
    const inputRef = useRef(null);
    const textareaRef = useRef(null);
    const containerRef = useRef(null);
    const focusableContainerRef = useRef(null);
    const focusTimeoutRef = useRef(null);
    const blurTimeoutRef = useRef(null);
    const focusReturnTimeoutRef = useRef(null);
    const focusSuccessTimeoutRef = useRef(null);
    const successTimeoutRef = useRef(null);
    const errorRefocusTimeoutRef = useRef(null);
    const validationErrorRefocusTimeoutRef = useRef(null);
    const isDateTime = ['date', 'time', 'datetime-local'].includes(type);
    const showSpinner = useSpinDelay(isLoading, { delay: 250, minDuration: 250 });
    const useTextarea = multiline && !isCurrency && !isNumeric && !isDateTime;
    const isActiveElementInInlineEditableText = useCallback(() => {
        const activeElement = document.activeElement;
        if (!activeElement)
            return false;
        const isEditableInput = (activeElement.tagName === 'INPUT' ||
            activeElement.tagName === 'TEXTAREA');
        if (!isEditableInput)
            return false;
        let current = activeElement.parentElement;
        while (current) {
            if (current.querySelector &&
                current.querySelector('[role="button"]') &&
                current.classList.contains('group')) {
                return true;
            }
            current = current.parentElement;
        }
        return false;
    }, []);
    const shouldManageFocus = useCallback(() => {
        const activeElement = document.activeElement;
        const container = containerRef.current;
        const focusableContainer = focusableContainerRef.current;
        if (!container || !activeElement)
            return false;
        if (container.contains(activeElement) || activeElement === focusableContainer) {
            return true;
        }
        if (isEditing && (activeElement === inputRef.current || activeElement === textareaRef.current)) {
            return true;
        }
        if (isActiveElementInInlineEditableText()) {
            return false;
        }
        const isInFormField = (activeElement.tagName === 'INPUT' ||
            activeElement.tagName === 'TEXTAREA' ||
            activeElement.tagName === 'SELECT' ||
            activeElement.role === 'textbox');
        if (isInFormField) {
            return false;
        }
        return activeElement === document.body;
    }, [isEditing, isActiveElementInInlineEditableText]);
    const clearFocusTimeouts = useCallback(() => {
        if (focusTimeoutRef.current)
            clearTimeout(focusTimeoutRef.current);
        if (focusReturnTimeoutRef.current)
            clearTimeout(focusReturnTimeoutRef.current);
        if (focusSuccessTimeoutRef.current)
            clearTimeout(focusSuccessTimeoutRef.current);
        if (errorRefocusTimeoutRef.current)
            clearTimeout(errorRefocusTimeoutRef.current);
        if (validationErrorRefocusTimeoutRef.current)
            clearTimeout(validationErrorRefocusTimeoutRef.current);
    }, []);
    const clearAllFocusTimeouts = useCallback(() => {
        clearFocusTimeouts();
        if (blurTimeoutRef.current)
            clearTimeout(blurTimeoutRef.current);
    }, [clearFocusTimeouts]);
    const formatDisplayValue = useCallback((val) => {
        if (!val && val !== 0)
            return '';
        if (val === undefined || val === null)
            return '';
        const stringValue = String(val);
        if (displayFormatter) {
            return displayFormatter(val);
        }
        if (isNumeric && !formatPattern) {
            const numValue = typeof val === 'string' ? parseFloat(val) : val;
            if (!isNaN(numValue)) {
                return new Intl.NumberFormat((navigator.language || 'en-US'), {
                    useGrouping: !!thousandSeparator,
                    minimumFractionDigits: 0,
                    maximumFractionDigits: decimalScale
                }).format(numValue);
            }
        }
        if (formatPattern) {
            let formatted = '';
            let valueIndex = 0;
            const cleanValue = stringValue.replace(/[^\w]/g, '');
            for (let i = 0; i < formatPattern.length && valueIndex < cleanValue.length; i++) {
                if (formatPattern[i] === '#') {
                    formatted += cleanValue[valueIndex];
                    valueIndex++;
                }
                else {
                    formatted += formatPattern[i];
                }
            }
            return formatted || stringValue;
        }
        return stringValue;
    }, [isNumeric, formatPattern, thousandSeparator, decimalScale, displayFormatter]);
    const displayContent = useMemo(() => {
        const hasValue = value !== undefined && value !== null && value !== '';
        const isZero = value === 0;
        if (!hasValue && !isZero) {
            return placeholder;
        }
        if (isDateTime) {
            const getDateDisplayVariant = (inputType) => {
                switch (inputType) {
                    case 'date': return 'date';
                    case 'time': return 'time';
                    case 'datetime-local': return 'datetime';
                    default: return 'datetime';
                }
            };
            return (_jsx(DateDisplay, { utcValue: String(value), className: "text-inherit", variant: getDateDisplayVariant(type) }));
        }
        if (isCurrency) {
            const numValue = typeof value === 'string' ? parseFloat(value) : value;
            if (!isNaN(numValue)) {
                return (_jsx(CurrencyDisplay, { value: numValue, currency: currency, className: "text-inherit" }));
            }
        }
        const formatted = formatDisplayValue(value);
        return formatted || placeholder;
    }, [value, placeholder, isDateTime, isCurrency, formatDisplayValue, type, currency]);
    const startEditing = useCallback(() => {
        if (disabled || readOnly)
            return;
        clearFocusTimeouts();
        setIsEditing(true);
        setInternalError(null);
        setShowSuccess(false);
        const safeValue = value ?? '';
        const editingValue = isDateTime ? utcToInputFormat(String(safeValue), type) : (safeValue ?? '');
        setEditValue(editingValue);
        focusTimeoutRef.current = setTimeout(() => {
            if (useTextarea && textareaRef.current) {
                textareaRef.current.focus();
                textareaRef.current.select();
            }
            else if (inputRef.current) {
                inputRef.current.focus();
                inputRef.current.select();
            }
        }, 0);
    }, [disabled, readOnly, value, useTextarea, isDateTime, type, clearFocusTimeouts]);
    const cancelEditing = useCallback(() => {
        clearAllFocusTimeouts();
        const shouldManageFocusNow = shouldManageFocus();
        setIsEditing(false);
        setEditValue(value ?? '');
        setInternalError(null);
        onCancel?.();
        if (shouldManageFocusNow) {
            const focusableElement = focusableContainerRef.current;
            if (focusableElement) {
                focusReturnTimeoutRef.current = setTimeout(() => {
                    if (shouldManageFocus()) {
                        focusableElement.focus();
                    }
                }, 0);
            }
        }
    }, [value, onCancel, clearAllFocusTimeouts, shouldManageFocus]);
    const saveValue = useCallback(async () => {
        const saveableValue = isDateTime
            ? inputFormatToUtc(String(editValue), type)
            : editValue;
        setIsLoading(true);
        setInternalError(null);
        const currentValue = value ?? '';
        const normalizedSaveableValue = isDateTime ? saveableValue : String(saveableValue);
        const normalizedCurrentValue = isDateTime ? String(currentValue) : String(currentValue);
        if (normalizedSaveableValue === normalizedCurrentValue) {
            setIsLoading(false);
            cancelEditing();
            return;
        }
        const stringValue = String(editValue);
        if (required && !stringValue.trim()) {
            setIsLoading(false);
            setInternalError(L.component_library.inline_editable_text.field_required);
            const element = useTextarea ? textareaRef.current : inputRef.current;
            if (element) {
                validationErrorRefocusTimeoutRef.current = setTimeout(() => {
                    element.focus();
                    element.select();
                }, 0);
            }
            return;
        }
        try {
            await onSave?.(saveableValue);
            setIsEditing(false);
            setShowSuccess(true);
            const focusableElement = focusableContainerRef.current;
            if (focusableElement && shouldManageFocus()) {
                focusSuccessTimeoutRef.current = setTimeout(() => {
                    if (shouldManageFocus()) {
                        focusableElement.focus();
                    }
                }, 0);
            }
            successTimeoutRef.current = setTimeout(() => {
                setShowSuccess(false);
            }, 2500);
        }
        catch (err) {
            setInternalError(err instanceof Error ? err.message : L.component_library.inline_editable_text.failed_to_save);
            const element = useTextarea ? textareaRef.current : inputRef.current;
            if (element) {
                errorRefocusTimeoutRef.current = setTimeout(() => {
                    element.focus();
                    element.select();
                }, 0);
            }
        }
        finally {
            setIsLoading(false);
        }
    }, [editValue, value, required, onSave, cancelEditing, isDateTime, type, shouldManageFocus, useTextarea]);
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            cancelEditing();
        }
        else if (e.key === 'Enter') {
            e.preventDefault();
            saveValue();
        }
        else if (e.key === 'Tab') {
        }
    }, [cancelEditing, saveValue]);
    const handleBlur = useCallback(() => {
        blurTimeoutRef.current = setTimeout(() => {
            const activeEl = document.activeElement;
            const inputEl = inputRef.current;
            const textareaEl = textareaRef.current;
            if (activeEl !== inputEl && activeEl !== textareaEl) {
                saveValue();
            }
        }, 100);
    }, [saveValue]);
    const handleContainerKeyDown = useCallback((e) => {
        if (!isEditing && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            startEditing();
        }
    }, [isEditing, startEditing]);
    useEffect(() => {
        const handleGlobalFocusChange = () => {
            const activeElement = document.activeElement;
            const container = containerRef.current;
            if (container && !container.contains(activeElement)) {
                clearFocusTimeouts();
                if (isActiveElementInInlineEditableText() ||
                    (activeElement && (activeElement.tagName === 'INPUT' ||
                        activeElement.tagName === 'TEXTAREA' ||
                        activeElement.tagName === 'SELECT' ||
                        activeElement.role === 'textbox'))) {
                    clearFocusTimeouts();
                }
            }
        };
        document.addEventListener('focusin', handleGlobalFocusChange);
        return () => {
            document.removeEventListener('focusin', handleGlobalFocusChange);
        };
    }, [clearFocusTimeouts, isActiveElementInInlineEditableText]);
    const showPlaceholder = Boolean(placeholder && (value === undefined ||
        value === null ||
        value === '' ||
        displayContent === placeholder));
    const feedbackIcons = useMemo(() => {
        const iconPaddingClass = Component === 'h1' ? 'pt-[8px]' :
            Component === 'h2' ? 'pt-[6px]' :
                Component === 'h3' ? 'pt-[4px]' : 'pt-[1px]';
        const spinnerPaddingClass = Component === 'h1' ? 'pt-[6px]' :
            Component === 'h2' ? 'pt-[5px]' :
                Component === 'h3' ? 'pt-[2px]' : 'pt-[2px]';
        if (showSpinner) {
            return _jsx(Spinner, { size: Component === 'span' ? 'xsmall' : 'small', className: `p-0 ${spinnerPaddingClass}` });
        }
        if (showSuccess && !isEditing) {
            return (_jsxs("div", { className: cn("text-green-900", iconPaddingClass, styles.fadeInAnimation), children: [_jsx(Icon, { name: "CheckCircleIcon", type: "solid", size: "md" }), _jsx("span", { className: "sr-only", "aria-live": "polite", children: L.component_library.inline_editable_text.changes_saved })] }));
        }
        if (!disabled && !readOnly && !isEditing) {
            return (_jsx("div", { className: iconPaddingClass, children: _jsx(Icon, { name: "PencilSquareIcon", type: "solid", size: "md", className: "text-gray-800 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity duration-100" }) }));
        }
        return null;
    }, [showSpinner, showSuccess, isEditing, disabled, readOnly, Component]);
    const EditableDisplay = useMemo(() => {
        if (isEditing)
            return null;
        const getTextStyles = () => {
            const styles = {
                h1: 'text-3xl font-bold !m-0',
                h2: 'text-2xl font-bold !m-0',
                h3: 'text-xl font-bold !m-0',
                span: 'text-base'
            };
            return styles[Component] || styles.span;
        };
        return (_jsx(Component, { className: cn(getTextStyles(), showPlaceholder && "text-gray-500", useTextarea ? "whitespace-pre-wrap min-w-0" : "truncate min-w-0 flex-1"), style: useTextarea ? {
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
            } : undefined, children: displayContent }));
    }, [
        isEditing,
        Component,
        showPlaceholder,
        useTextarea,
        displayContent
    ]);
    useEffect(() => {
        if (!isEditing) {
            setEditValue(value ?? '');
        }
    }, [value, isEditing]);
    useEffect(() => {
        if (error?.hasError) {
            setInternalError(error.errorMessage);
        }
        else if (!error?.hasError) {
            setInternalError(null);
        }
    }, [error]);
    useEffect(() => {
        return () => {
            clearAllFocusTimeouts();
            if (successTimeoutRef.current)
                clearTimeout(successTimeoutRef.current);
        };
    }, [clearAllFocusTimeouts]);
    return (_jsxs("div", { ref: ref, className: cn(className), children: [_jsx("div", { ref: containerRef, onClick: !isEditing ? startEditing : undefined, children: _jsx("div", { className: cn("group inline-flex items-start gap-2 max-w-full", "border-2 border-transparent px-2 py-1 rounded-md bg-gray-50", isLoading && `${styles.pulseRing} text-gray-700 bg-gray-100`, !isLoading && !isEditing && "shadow-[inset_0_0_0_1px_rgb(209_213_219)]", isEditing && !isLoading && "shadow-[inset_0_0_0_2px_rgb(79_70_229)]", isEditing && !isLoading && showError && "shadow-[inset_0_0_0_2px_rgb(220_38_38)]", disabled && !isLoading && "text-gray-500", !isEditing && !disabled && !readOnly && "cursor-pointer", showSuccess && !isEditing && `${styles.flashAnimation}`), ref: focusableContainerRef, onKeyDown: handleContainerKeyDown, tabIndex: !isEditing && !disabled && !readOnly ? 0 : -1, role: !isEditing ? "button" : undefined, "aria-label": ariaLabel || L.component_library.inline_editable_text.edit_field, children: useTextarea ? (_jsxs("div", { className: cn("inline-flex items-start gap-2 max-w-full"), children: [_jsx("div", { className: "relative min-w-0 flex-1", children: isEditing ? (_jsx("div", { className: "", children: _jsx(AutosizedTextarea, { value: editValue.toString(), label: ariaLabel, onBlur: handleBlur, onKeyDown: handleKeyDown, disabled: isLoading, onChange: (e) => {
                                            const newValue = e.target.value;
                                            setEditValue(newValue);
                                            setInternalError(null);
                                        }, textElement: Component }) })) : (EditableDisplay) }), _jsx("div", { className: `flex justify-center flex-shrink-0 w-7`, children: feedbackIcons })] })) : (_jsxs("div", { className: cn("inline-flex items-start min-w-0 max-w-full"), children: [isEditing ? (_jsx("div", { className: "min-w-0 flex-1 overflow-hidden", children: _jsx(AutosizedInput, { ref: inputRef, value: editValue, placeholder: placeholder, textElement: Component, disabled: isLoading, required: required, ariaLabel: ariaLabel || `${L.component_library.inline_editable_text.edit_field}: ${label}`, onKeyDown: handleKeyDown, onBlur: handleBlur, onChange: (e) => {
                                        const newValue = e.target.value;
                                        setEditValue(newValue);
                                        setInternalError(null);
                                    }, error: showError ? { hasError: true, errorMessage: errorMessage } : undefined, isNumeric: isNumeric, isCurrency: isCurrency, formatPattern: formatPattern, mask: mask, currencySymbol: getCurrencySymbol(currency, (navigator.language || "en-US")), decimalScale: decimalScale, thousandSeparator: thousandSeparator, decimalSeparator: decimalSeparator, allowNegative: allowNegative, valueIsNumericString: valueIsNumericString, numberFormatProps: numberFormatProps, type: type }) })) : (EditableDisplay), _jsx("div", { className: cn(`flex justify-center flex-shrink-0 w-7`), children: feedbackIcons })] })) }) }), showError && errorMessage && (_jsx("div", { className: "text-xs text-red-600", role: "alert", "aria-live": "polite", children: errorMessage }))] }));
}));
